import type { Team } from '../lib/session';
import argentina from '../assets/flags/argentina.svg';
import brazil from '../assets/flags/brazil.svg';
import england from '../assets/flags/england.svg';
import france from '../assets/flags/france.svg';
import italy from '../assets/flags/italy.svg';
import japan from '../assets/flags/japan.svg';
import netherlands from '../assets/flags/netherlands.svg';
import spain from '../assets/flags/spain.svg';

/**
 * A team's flag. Since D53 this is the team's mark everywhere the suit glyph
 * used to be — the tile foot, Profile, Now, the admin table, the reveal.
 *
 * **`<img>` rather than inline SVG, and that is not a style preference.** The
 * Brazilian and Argentine flags are the only complex ones in the set, and both
 * are built out of `<defs>` with short ids — `#c`, `#s`, `#t`, `#ray1`. Inlined
 * into one document those ids collide with each other and with anything else on
 * the page, and the failure mode is a flag rendering with another flag's stars.
 * An `<img>` gives each file its own document, and Vite inlines the six small
 * ones as data URIs at build time anyway, so this costs two requests, not eight.
 *
 * All eight are the official SVGs from Wikimedia Commons, all public domain.
 * Spain is the **civil flag**, without the coat of arms: the arms version is
 * 156 kB against 185 bytes, and at the 13px this renders at on a tile the
 * heraldry is an illegible smudge. See `docs/decisions.md` D53.
 */
const SRC: Record<Team, string> = {
  Brazil: brazil,
  Netherlands: netherlands,
  Italy: italy,
  Japan: japan,
  England: england,
  Spain: spain,
  France: france,
  Argentina: argentina,
};

interface Props {
  team: Team;
  /** Rendered height in px. Width follows the flag's own ratio — they are not
   *  uniform (Brazil is 10:7, England 5:3, Argentina 8:5, the rest 3:2), and
   *  forcing a common box would visibly stretch three of the eight. */
  height?: number;
  className?: string;
  /**
   * Set when the country name is already on screen beside it, as on the reveal
   * and in Profile. The flag is then decoration and repeating the name is noise
   * for a screen reader. Left false on the photo tile and the admin table,
   * where the flag is the **only** thing carrying the team.
   */
  decorative?: boolean;
}

export default function Flag({ team, height = 13, className, decorative = false }: Props) {
  return (
    <img
      className={`flag${className ? ` ${className}` : ''}`}
      src={SRC[team]}
      height={height}
      style={{ height }}
      alt={decorative ? '' : team}
      aria-hidden={decorative || undefined}
      draggable={false}
      loading="lazy"
      decoding="async"
    />
  );
}
